import React, {useEffect} from 'react'
import clamp from 'lodash/clamp'
import {useAnimation} from 'motion/react'

import {usePreviousValue} from '@vio/core/src/utils/react/hooks/usePreviousValue'

import {DOT_WIDTH} from './constant'
import {DotIndicator, IndicatorWrapper} from './styles'

type Props = {
  amount: number
  currentIndex: number
  isRtl: boolean
}

const calculateDotPosition = (i: number, currentIndex: number) =>
  -(currentIndex * DOT_WIDTH) + i * DOT_WIDTH - DOT_WIDTH / 2

const getIndicatorStyle = ({
  indicatorIndex,
  currentIndex,
  isRtl
}: {
  indicatorIndex: number
  currentIndex: number
  isRtl: boolean
}) => ({
  x: (isRtl ? -1 : 1) * calculateDotPosition(indicatorIndex, currentIndex),
  scale: 1 / clamp(Math.abs(indicatorIndex - currentIndex), 1, 7),
  display: Math.abs(indicatorIndex - currentIndex) > 3 ? 'none' : 'block',
  opacity: indicatorIndex === currentIndex ? 1 : 0.5
})

export const CurrentSlideIndicators = ({
  currentIndex,
  amount,
  isRtl
}: Props) => {
  const previousIndex = usePreviousValue(currentIndex)
  const controls = useAnimation()

  useEffect(() => {
    const immediate = Math.abs((previousIndex ?? 0) - currentIndex) > 1 // No animation when we roll over to/from 0
    controls.start((indicatorIndex: number) => ({
      ...getIndicatorStyle({indicatorIndex, currentIndex, isRtl}),
      ...(immediate
        ? {
            transition: {
              duration: 0
            }
          }
        : {
            transition: {
              type: 'spring',
              bounce: 0
            }
          })
    }))
  }, [currentIndex, controls, previousIndex, isRtl])

  return (
    <IndicatorWrapper>
      {[...new Array(amount)].map((_, indicatorIndex) => {
        return (
          <DotIndicator
            key={indicatorIndex}
            custom={indicatorIndex}
            initial={getIndicatorStyle({indicatorIndex, currentIndex, isRtl})}
            animate={controls}
          />
        )
      })}
    </IndicatorWrapper>
  )
}
